import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { AiIcon } from "@v2/icons";
import { TypewriterLine } from "@v2/components/animated/TypewriterLine";
import { ShimmerText } from "@webV2/pages/Tessa/ShimmerText";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";

/**
 * AI Search Header
 *
 * Shows a logo/icon, the assistant name (with shimmer glow), and a rotating
 * typewriter-style headline of suggested prompts.
 *
 * TailwindCSS + Framer Motion.
 */
export interface AiSearchHeaderProps {
  /** Optional logo element (e.g., <img/>, an SVG, or an icon component). */
  logo?: React.ReactNode;
  /** Assistant name to display. Default: "Tessa" */
  name?: string;
  /**
   * Suggestions to rotate through beneath the title.
   * Example: ["Find meeting notes", "Summarize this PDF", "Draft an email"]
   */
  suggestions?: string[];
  /** Milliseconds per character while typing. Default: 35 */
  typingSpeedMs?: number;
  /** Milliseconds to hold the full sentence before deleting. Default: 1400 */
  holdMs?: number;
  /** Optional className for outer wrapper. */
  className?: string;

  isVisible?: boolean;
}

export default function AiSearchHeader({
  logo,
  name = "Tessa",
  suggestions = [
    "What meetings are we having today?",
    "Where is the meeting room?",
    "Where is @John today?"
  ],
  typingSpeedMs = 35,
  holdMs = 1400,
  className,
  isVisible: controlledIsVisible = true
}: AiSearchHeaderProps) {
  const { isVisible } = useCloseAnimationTimeout(controlledIsVisible);

  if (!isVisible) return null;

  return (
    <div className={"w-full"}>
      {/* Local keyframes for shimmer & caret */}
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes caret {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
      `}</style>

      <div className={["flex items-center gap-4", className ?? ""].join(" ")}>
        <div className={"shrink-0"}>
          <AiIcon className={"size-14"} />
        </div>
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <ShimmerText
              text={"Tessa"}
              className="font-semibold text-2xl tracking-tight drop-shadow-[0_0_18px_rgba(99,102,241,0.45)] sm:text-3xl md:text-4xl"
            />
          </div>

          <TypewriterLine
            items={suggestions}
            typingSpeedMs={typingSpeedMs}
            holdMs={holdMs}
            className="mt-1 whitespace-nowrap text-muted-foreground/90 text-sm sm:text-base md:text-lg"
          />
        </div>
      </div>
    </div>
  );
}
